"use client";

import React, { Suspense, useContext, useEffect, useState } from "react";
import { CircularProgress, Container, Grid } from "@mui/material";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "../../App.css";
import UserProfile from "../../pages/Manage/user/UserProfile.jsx";
import UserContext from "../context/User";
import { useDispatch } from "react-redux";
import { login as loginAction, logout as logoutAction } from "../../feature/userSlice";
import { decryptSearchParams } from "../../utils/queryParam.js";
import TagsDetails from "../Qna/TagsDetails.js";
import SearchDetails from "../Qna/SearchDetails.js";
import User from "../../pages/Manage/user/User.jsx";
import UserSaveDetails from "../../pages/Manage/user/UserSaveDetails.jsx";
import UserHome from "../../pages/Manage/user/UserHome.jsx";
import Sidebar from "../Qna/Sidebar";

const Login = React.lazy(() => import("../userlogin/Login"));
const Authentication = React.lazy(() => import("../userlogin/Authentication"));
const Register = React.lazy(() => import("../userlogin/Register"));
const ForgotUsername = React.lazy(() => import("../userlogin/ForgotUsername"));
const ForgotPassword = React.lazy(() => import("../userlogin/ForgotPassword.jsx"));
const CreatePassword = React.lazy(() => import("../userlogin/CreatePassword"));
const Unauthorize = React.lazy(() => import("../userlogin/Unauthorize"));
const Qna = React.lazy(() => import("../Qna"));
const AddQuestion = React.lazy(() => import("../AddQuestion"));
const Home = React.lazy(() => import("../Qna/Home.js"));
const ViewQuestion = React.lazy(() => import("../ViewQuestion"));
const Header = React.lazy(() => import("../Header"));
const Footer = React.lazy(() => import("../Footer/Footer.js"));
const Tags = React.lazy(() => import("../Qna/Tags.js"));
const UserDetails = React.lazy(() => import("../../pages/Manage/user/UserDetails.jsx"));

const FullScreenLoader = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      width: "100vw",
    }}
  >
    <CircularProgress size={100} thickness={1} />
  </div>
);

function RootRoutes() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const UserConsumer = useContext(UserContext);
  const { handleLogin } = UserConsumer;
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const callApiWithQueryParams = async (token, username, password) => {
    try {
      if (token && username && password) {
        localStorage.setItem("iframe", "true");
        localStorage.setItem("isPortalView", "true");
        const loginUser = { username, password };
        const loggedStatus = await handleLogin(loginUser);
        console.log("Query param login response:", loggedStatus);

        if (
          loggedStatus?.isLoggedIn &&
          loggedStatus?.fullDetails?.status?.toLowerCase() === "active"
        ) {
          const jwtToken = loggedStatus.fullDetails.token;
          if (!jwtToken) {
            throw new Error("No token found in query param login response");
          }
          localStorage.setItem("UserDetails", JSON.stringify(loggedStatus.fullDetails));
          localStorage.setItem("jwtToken", jwtToken);
          localStorage.setItem("isLoggedIn", "true");
          dispatch(loginAction(loggedStatus.fullDetails));
          console.log("Query param login - Token saved:", jwtToken);
          navigate("/");
        } else {
          throw new Error(loggedStatus?.message || "Query param login failed");
        }
      } else {
        throw new Error("Invalid query parameters for login");
      }
    } catch (error) {
      console.error("Query param login failed:", error.message);
      // navigate("/login");
      navigate("/");
    }
  };

  useEffect(() => {
  const params = new URLSearchParams(location.search);
  const search = params.get("search");
  let userParam = params.get("user");

  // Handle user parameter with nested query-like structure
  let cleanedUserParam = userParam;
  let additionalSearchParam = null;
  if (userParam && userParam.includes("?")) {
    // Split userParam at the first '?'
    [cleanedUserParam, additionalSearchParam] = userParam.split("?", 2);
  }

  console.log("search param:", search);
  console.log("user param:", userParam);
  console.log("cleaned user param:", cleanedUserParam);
  console.log("additional search param:", additionalSearchParam);

  // Treat missing search or literal "null" as "no search" (Portal logout → clear Forum session)
  const hasNoSearch =
    search === null ||
    search === undefined ||
    search === "" ||
    String(search).toLowerCase() === "null";

  // Clear only user/login data when search=null; keep iframe & isPortalView so Forum still shows as portal (no header)
  if (hasNoSearch && cleanedUserParam?.toLowerCase() === "portalview") {
    console.log("Portal view with search=null - Resetting user/token data, keeping iframe & isPortalView");
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("oldLoginCrypt");
    localStorage.removeItem("defLoginCrypt");
    localStorage.removeItem("defaultToken");
    localStorage.removeItem("loginData");
    localStorage.removeItem("queryToken");
    localStorage.removeItem("loggedIn");
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("dashboard_status");
    localStorage.removeItem("firstName");
    localStorage.removeItem("userOrganisation");
    localStorage.removeItem("UserDetails");
    // Do NOT remove "iframe" or "isPortalView" - keep so it still loads as portal page (no appbar/header)
    // Explicitly set loggedIn to false so any code that reads it sees unauthenticated state
    localStorage.setItem("loggedIn", "false");
    dispatch(logoutAction());
  }

  // Set isPortalView and iframe when opened from Portal (so layout shows as portal, no header)
  if (cleanedUserParam && cleanedUserParam.toLowerCase() === "portalview") {
    localStorage.setItem("isPortalView", "true");
    localStorage.setItem("iframe", "true");
  }

  // Handle login if search params contain token, username, and password
  let decryptParam = null;
  if (search) {
    decryptParam = decryptSearchParams(search);
    console.log("decrypt param:", decryptParam);
  } else if (additionalSearchParam) {
    decryptParam = decryptSearchParams(additionalSearchParam);
    console.log("decrypt param (from user):", decryptParam);
  }

  if (
    decryptParam &&
    decryptParam.token &&
    decryptParam.username &&
    decryptParam.password
  ) {
    localStorage.setItem("iframe", "true");
    callApiWithQueryParams(
      decryptParam.token,
      decryptParam.username,
      decryptParam.password
    );
  }
}, [location, handleLogin, dispatch, navigate]);

  // useEffect(() => {
  //   const params = new URLSearchParams(location.search);
  //   const search = params.get("search");
  //   let userParam = params.get("user");

  //   // Handle user parameter with nested query-like structure
  //   let cleanedUserParam = userParam;
  //   let additionalSearchParam = null;
  //   if (userParam && userParam.includes("?")) {
  //     // Split userParam at the first '?'
  //     [cleanedUserParam, additionalSearchParam] = userParam.split("?", 2);
  //   }

  //   console.log("search param:", search);
  //   console.log("user param:", userParam);
  //   console.log("cleaned user param:", cleanedUserParam);
  //   console.log("additional search param:", additionalSearchParam);

  //   // Store query params in localStorage
  //   // if (search) {
  //   //   localStorage.setItem("searchParams", search);
  //   // } else if (additionalSearchParam) {
  //   //   localStorage.setItem("searchParams", additionalSearchParam);
  //   // } else {
  //   //   localStorage.removeItem("searchParams");
  //   // }

  //   // if (cleanedUserParam) {
  //   //   localStorage.setItem("userParam", cleanedUserParam);
  //   // } else {
  //   //   localStorage.removeItem("userParam");
  //   // }

  //   // Set isPortalView based on cleaned user=portalview
  //   if (cleanedUserParam && cleanedUserParam.toLowerCase() === "portalview") {
  //     localStorage.setItem("isPortalView", "true");
  //   }

  //   // Handle login if search params contain token, username, and password
  //   let decryptParam = null;
  //   if (search) {
  //     decryptParam = decryptSearchParams(search);
  //     console.log("decrypt param:", decryptParam);
  //   } else if (additionalSearchParam) {
  //     decryptParam = decryptSearchParams(additionalSearchParam);
  //     console.log("decrypt param (from user):", decryptParam);
  //   }

  //   if (
  //     decryptParam &&
  //     decryptParam.token &&
  //     decryptParam.username &&
  //     decryptParam.password
  //   ) {
  //     localStorage.setItem("iframe", "true");
  //     callApiWithQueryParams(
  //       decryptParam.token,
  //       decryptParam.username,
  //       decryptParam.password
  //     );
  //   }
  // }, [location, handleLogin, dispatch, navigate]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isPortalView = localStorage.getItem("isPortalView") === "true";
  const iframe = localStorage.getItem("iframe") === "true";
  const isMobile = windowWidth <= 768;

  console.log("isPortalView:", isPortalView);
  console.log("iframe:", iframe);

  const Layout = ({ children }) => {
    return (
      <Container
        maxWidth="lg"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          px: { xs: 1, sm: 2, md: 3 },
          py: { xs: 0, md: 0 },
          mt: { xs: 0, md: "-64px" },
          position: "relative",
          top: isPortalView ? 0 : "64px",
          pb: { xs: 8, md: 8 },
        }}
      >
        <Grid
          container
          sx={{ flex: 1, flexDirection: { xs: "column", md: "row" } }}
        >
          <Grid
            item
            xs={12}
            md={2}
            sx={{
              height: { xs: "auto", md: "calc(100vh)" },
              overflowY: "auto",
              position: { xs: "relative", md: "sticky" },
              top: { md: 0 },
              bgcolor: "#fff",
              borderRight: { md: "1px solid #e3e6e8" },
              zIndex: 1000,
              mb: { xs: 2, md: 0 },
              pt: { xs: 0, md: "64px" },
            }}
          >
            <Sidebar />
          </Grid>
          <Grid
            item
            xs={12}
            md={iframe ? 10 : 10}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              px: { xs: 1, sm: 2 },
              pt: { xs: 0, md: "64px" },
              pb: { xs: 2, md: 0 },
            }}
          >
            {children}
          </Grid>
        </Grid>
      </Container>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <Suspense fallback={<FullScreenLoader />}>
        {!isPortalView && (
          <div
            style={{ width: "100%", position: "fixed", top: 0, zIndex: 1100 }}
          >
            <Header />
          </div>
        )}
        <div
          style={{
            flex: "1 0 auto",
            display: "flex",
            flexDirection: "column",
            width: "100%",
            marginTop: isPortalView ? 0 : 0,
          }}
        >
          <Layout>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/account/*" element={<Authentication />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot/username" element={<ForgotUsername />} />
              <Route path="/forgot/password" element={<ForgotPassword />} />
              <Route path="/forgot/password/create/" element={<CreatePassword />} />
              <Route path="/unauthorize" element={<Unauthorize />} />
              <Route path="/allquestion" element={<Qna />} />
              <Route path="/add-question" element={<AddQuestion />} />
              <Route path="/question" element={<ViewQuestion />} />
              <Route path="/" element={<Home />} />
              <Route path="/tags" element={<Tags />} />
              <Route path="/tags/:tagName" element={<TagsDetails />} />
              <Route path="/userdetails" element={<UserDetails />} />
              <Route path="/userprofile" element={<UserProfile />} />
              <Route path="/usersave" element={<UserSaveDetails />} />
              <Route path="/userhome" element={<UserHome />} />
              <Route path="/user1" element={<User />} />
              <Route path="/search/:query" element={<SearchDetails />} />
              <Route path="*" element={<Unauthorize />} />
            </Routes>
          </Layout>
        </div>
        <div
          style={{
            width: "100%",
            flexShrink: 0,
            zIndex: 1000,
          }}
        >
          <Footer />
        </div>
      </Suspense>
    </div>
  );
}

export default RootRoutes;

// ---------------WORKING CODE WITH IFRAME ---------------------
// "use client";

// import React, { Suspense, useContext, useEffect, useState } from "react";
// import { CircularProgress, Container, Grid } from "@mui/material";
// import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
// import "../../App.css";
// import UserProfile from "../../pages/Manage/user/UserProfile.jsx";
// import UserContext from "../context/User";
// import { useDispatch } from "react-redux";
// import { login as loginAction } from "../../feature/userSlice";
// import { decryptSearchParams } from "../../utils/queryParam.js";
// import TagsDetails from "../Qna/TagsDetails.js";
// import SearchDetails from "../Qna/SearchDetails.js";
// import User from "../../pages/Manage/user/User.jsx";
// import UserSaveDetails from "../../pages/Manage/user/UserSaveDetails.jsx";
// import UserHome from "../../pages/Manage/user/UserHome.jsx";
// import Sidebar from "../Qna/Sidebar"; // Import Sidebar component

// const Login = React.lazy(() => import("../userlogin/Login"));
// const Authentication = React.lazy(() => import("../userlogin/Authentication"));
// const Register = React.lazy(() => import("../userlogin/Register"));
// const ForgotUsername = React.lazy(() => import("../userlogin/ForgotUsername"));
// const ForgotPassword = React.lazy(() =>
//   import("../userlogin/ForgotPassword.jsx")
// );
// const CreatePassword = React.lazy(() => import("../userlogin/CreatePassword"));
// const Unauthorize = React.lazy(() => import("../userlogin/Unauthorize"));
// const Qna = React.lazy(() => import("../Qna"));
// const AddQuestion = React.lazy(() => import("../AddQuestion"));
// const Home = React.lazy(() => import("../Qna/Home.js"));
// const ViewQuestion = React.lazy(() => import("../ViewQuestion"));
// const Header = React.lazy(() => import("../Header"));
// const Footer = React.lazy(() => import("../Footer/Footer.js"));
// const Tags = React.lazy(() => import("../Qna/Tags.js"));
// const UserDetails = React.lazy(() =>
//   import("../../pages/Manage/user/UserDetails.jsx")
// );

// const FullScreenLoader = () => (
//   <div
//     style={{
//       display: "flex",
//       justifyContent: "center",
//       alignItems: "center",
//       height: "100vh",
//       width: "100vw",
//     }}
//   >
//     <CircularProgress size={100} thickness={1} />
//   </div>
// );

// function RootRoutes() {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const UserConsumer = useContext(UserContext);
//   const { handleLogin } = UserConsumer;
//   const [windowWidth, setWindowWidth] = useState(window.innerWidth);

//   const callApiWithQueryParams = async (token, username, password) => {
//     try {
//       if (token && username && password) {
//         localStorage.setItem("iframe", "true");
//         const loginUser = {
//           username,
//           password,
//         };

//         const loggedStatus = await handleLogin(loginUser);
//         console.log("Query param login response:", loggedStatus);

//         if (
//           loggedStatus?.isLoggedIn &&
//           loggedStatus?.fullDetails?.status?.toLowerCase() === "active"
//         ) {
//           const jwtToken = loggedStatus.fullDetails.token;
//           if (!jwtToken) {
//             throw new Error("No token found in query param login response");
//           }
//           localStorage.setItem(
//             "UserDetails",
//             JSON.stringify(loggedStatus.fullDetails)
//           );
//           localStorage.setItem("jwtToken", jwtToken);
//           localStorage.setItem("isLoggedIn", true);
//           dispatch(loginAction(loggedStatus.fullDetails));
//           console.log("Query param login - Token saved:", jwtToken);
//           navigate("/");
//         } else {
//           throw new Error(loggedStatus?.message || "Query param login failed");
//         }
//       } else {
//         throw new Error("Invalid query parameters for login");
//       }
//     } catch (error) {
//       console.error("Query param login failed:", error.message);
//       navigate("/");
//     }
//   };

//   const iframe = localStorage.getItem("iframe");
//   // const isIframe =  true;
//   const isIframe = iframe === "true";
//   const isMobile = windowWidth <= 768;
//   useEffect(() => {
//     const params = new URLSearchParams(location.search);
//     const search = params.get("search");
//     const decryptParam = decryptSearchParams(search);
//     console.log("decrypt param!!", decryptParam);

//     if (
//       decryptParam &&
//       decryptParam.token &&
//       decryptParam.username &&
//       decryptParam.password
//     ) {
//       callApiWithQueryParams(
//         decryptParam.token,
//         decryptParam.username,
//         decryptParam.password
//       );
//     } else {
//       console.warn("No valid search parameter found.");
//       const publicRoutes = [
//         "/login",
//         "/search/:*",
//         "/question",
//         "/register",
//         "/forgot/username",
//         "/forgot/password",
//         "/forgot/password/create",
//         "/search/:query",
//         "/search/:tagName/:query",
//       ];
//       if (
//         !localStorage.getItem("jwtToken") &&
//         !publicRoutes.some((route) => {
//           const regex = new RegExp(
//             `^${route.replace(/:[\w]+/g, "[^/]+").replace(/:\*/g, ".*")}$`
//           );
//           return regex.test(location.pathname);
//         }) &&
//         location.pathname !== "/"
//       ) {
//         navigate("/", { replace: true });
//       }
//     }
//   }, [location, navigate, handleLogin, dispatch]);

//   useEffect(() => {
//     const handleResize = () => setWindowWidth(window.innerWidth);
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   // Define the Layout component with Container for responsiveness
//   const Layout = ({ children }) => {
//     return (
//       <Container
//         maxWidth="lg"
//         sx={{
//           flex: 1,
//           display: "flex",
//           flexDirection: "column",
//           px: { xs: 1, sm: 2, md: 3 },
//           py: { xs: 0, md: 0 },
//           mt: { xs: 0, md: "-64px" },
//           position: "relative",
//           top: isIframe ? 0 : "64px",
//           pb: { xs: 8, md: 8 }, // Add padding-bottom to prevent overlap with footer
//           // pb: { xs: 8, md: 10 }, // Add padding-bottom to prevent overlap with footer
//         }}
//       >
//         <Grid
//           container
//           sx={{ flex: 1, flexDirection: { xs: "column", md: "row" } }}
//         >
//           {/* Sidebar */}
//           {
//             <Grid
//               item
//               xs={12}
//               md={2}
//               sx={{
//                 height: { xs: "auto", md: "calc(100vh)" }, // Adjust height to account for header
//                 // height: { xs: "auto", md: "calc(100vh - 60px)" }, // Adjust height to account for header
//                 overflowY: "auto",
//                 position: { xs: "relative", md: "sticky" },
//                 top: { md: 0 },
//                 bgcolor: "#fff",
//                 // bgcolor: "#f8f9fa",
//                 borderRight: { md: "1px solid #e3e6e8" },
//                 zIndex: 1000,
//                 mb: { xs: 2, md: 0 },
//                 pt: { xs: 0, md: "64px" },
//               }}
//             >
//               <Sidebar />
//             </Grid>
//           }
//           {/* {!isIframe && (
//             <Grid
//               item
//               xs={12}
//               md={2}
//               sx={{
//                 height: { xs: "auto", md: "calc(100vh)" }, // Adjust height to account for header
//                 // height: { xs: "auto", md: "calc(100vh - 60px)" }, // Adjust height to account for header
//                 overflowY: "auto",
//                 position: { xs: "relative", md: "sticky" },
//                 top: { md: 0 },
//                 bgcolor: "#fff",
//                 // bgcolor: "#f8f9fa",
//                 borderRight: { md: "1px solid #e3e6e8" },
//                 zIndex: 1000,
//                 mb: { xs: 2, md: 0 },
//                 pt: { xs: 0, md: "64px" },
//               }}
//             >
//               <Sidebar />
//             </Grid>
//           )} */}
//           {/* Main Content */}
//           <Grid
//             item
//             xs={12}
//             md={isIframe ? 10 : 10}
//             // md={isIframe ? 12 : 10}
//             sx={{
//               flex: 1,
//               display: "flex",
//               flexDirection: "column",
//               px: { xs: 1, sm: 2 },
//               pt: { xs: 0, md: "64px" },
//               pb: { xs: 2, md: 0 }, // Add padding-bottom to main content
//             }}
//           >
//             {children}
//           </Grid>
//         </Grid>
//       </Container>
//     );
//   };

//   return (
//     <div
//       style={{
//         display: "flex",
//         flexDirection: "column",
//         minHeight: "100vh",
//         width: "100%",
//       }}
//     >
//       <Suspense fallback={<FullScreenLoader />}>
//         {!isIframe && (
//           <div
//             style={{ width: "100%", position: "fixed", top: 0, zIndex: 1100 }}
//           >
//             <Header />
//           </div>
//         )}
//         <div
//           style={{
//             flex: "1 0 auto",
//             display: "flex",
//             flexDirection: "column",
//             width: "100%",
//             marginTop: isIframe ? 0 : 0,
//           }}
//         >
//           <Layout>
//             <Routes>
//               <Route path="/login" element={<Login />} />
//               <Route path="/account/*" element={<Authentication />} />
//               <Route path="/register" element={<Register />} />
//               <Route path="/forgot/username" element={<ForgotUsername />} />
//               <Route path="/forgot/password" element={<ForgotPassword />} />
//               <Route
//                 path="/forgot/password/create/"
//                 element={<CreatePassword />}
//               />
//               <Route path="/unauthorize" element={<Unauthorize />} />
//               <Route path="/allquestion" element={<Qna />} />
//               <Route path="/add-question" element={<AddQuestion />} />
//               <Route path="/question" element={<ViewQuestion />} />
//               <Route path="/" element={<Home />} />
//               <Route path="/tags" element={<Tags />} />
//               <Route path="/tags/:tagName" element={<TagsDetails />} />
//               <Route path="/userdetails" element={<UserDetails />} />
//               <Route path="/userprofile" element={<UserProfile />} />
//               <Route path="/usersave" element={<UserSaveDetails />} />
//               <Route path="/userhome" element={<UserHome />} />
//               <Route path="/user1" element={<User />} />
//               <Route path="/search/:query" element={<SearchDetails />} />
//               <Route path="*" element={<Unauthorize />} />
//             </Routes>
//           </Layout>
//         </div>
//         <div
//           style={{
//             width: "100%",
//             flexShrink: 0,
//             zIndex: 1000, // Ensure footer is above other elements if needed
//           }}
//         >
//           <Footer />

//           {/* <Container maxWidth="lg">
//             <Footer />
//           </Container> */}
//         </div>
//       </Suspense>
//     </div>
//   );
// }

// export default RootRoutes;


// -------------------Previous code -------------------------
// // RootRoutes.js
// "use client"

// import React, { Suspense, useContext, useEffect, useState } from "react";
// import { CircularProgress, Container } from "@mui/material";
// import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
// import "../../App.css";
// import UserProfile from "../../pages/Manage/user/UserProfile.jsx";
// import UserContext from "../context/User";
// import { useDispatch } from "react-redux";
// import { login as loginAction } from "../../feature/userSlice";
// import { decryptSearchParams } from "../../utils/queryParam.js";
// import TagsDetails from "../Qna/TagsDetails.js";
// import SearchDetails from "../Qna/SearchDetails.js";
// import User from "../../pages/Manage/user/User.jsx";
// import UserSaveDetails from "../../pages/Manage/user/UserSaveDetails.jsx";
// import UserHome from "../../pages/Manage/user/UserHome.jsx";

// const Login = React.lazy(() => import("../userlogin/Login"));
// const Authentication = React.lazy(() => import("../userlogin/Authentication"));
// const Register = React.lazy(() => import("../userlogin/Register"));
// const ForgotUsername = React.lazy(() => import("../userlogin/ForgotUsername"));
// const ForgotPassword = React.lazy(() => import("../userlogin/ForgotPassword.jsx"));
// const CreatePassword = React.lazy(() => import("../userlogin/CreatePassword"));
// const Unauthorize = React.lazy(() => import("../userlogin/Unauthorize"));
// const Qna = React.lazy(() => import("../Qna"));
// const AddQuestion = React.lazy(() => import("../AddQuestion"));
// const Home = React.lazy(() => import("../Qna/Home.js"));
// const ViewQuestion = React.lazy(() => import("../ViewQuestion"));
// const Header = React.lazy(() => import("../Header"));
// const Footer = React.lazy(() => import("../Footer/Footer.js"));
// const Tags = React.lazy(() => import("../Qna/Tags.js"));
// const UserDetails = React.lazy(() => import("../../pages/Manage/user/UserDetails.jsx"));

// const FullScreenLoader = () => (
//   <div
//     style={{
//       display: "flex",
//       justifyContent: "center",
//       alignItems: "center",
//       height: "100vh",
//       width: "100vw",
//     }}
//   >
//     <CircularProgress size={100} thickness={1} />
//   </div>
// );

// function RootRoutes() {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const UserConsumer = useContext(UserContext);
//   const { handleLogin } = UserConsumer;
//   const [windowWidth, setWindowWidth] = useState(window.innerWidth);

//   const callApiWithQueryParams = async (token, username, password) => {
//     try {
//       if (token && username && password) {
//         localStorage.setItem("iframe", "true");
//         const loginUser = {
//           username,
//           password,
//         };

//         const loggedStatus = await handleLogin(loginUser);
//         console.log("Query param login response:", loggedStatus);

//         if (loggedStatus?.isLoggedIn && loggedStatus?.fullDetails?.status?.toLowerCase() === "active") {
//           const jwtToken = loggedStatus.fullDetails.token;
//           if (!jwtToken) {
//             throw new Error("No token found in query param login response");
//           }
//           localStorage.setItem("UserDetails", JSON.stringify(loggedStatus.fullDetails));
//           localStorage.setItem("jwtToken", jwtToken);
//           localStorage.setItem("isLoggedIn", true);
//           dispatch(loginAction(loggedStatus.fullDetails));
//           console.log("Query param login - Token saved:", jwtToken);
//           navigate("/");
//         } else {
//           throw new Error(loggedStatus?.message || "Query param login failed");
//         }
//       } else {
//         throw new Error("Invalid query parameters for login");
//       }
//     } catch (error) {
//       console.error("Query param login failed:", error.message);
//       navigate("/login");
//     }
//   };

//   const iframe = localStorage.getItem("iframe");
//   const isIframe = iframe === "true";
//   const isMobile = windowWidth <= 768;

//   useEffect(() => {
//     const params = new URLSearchParams(location.search);
//     const search = params.get("search");
//     const decryptParam = decryptSearchParams(search);
//     console.log("decrypt param!!", decryptParam);

//     if (decryptParam && decryptParam.token && decryptParam.username && decryptParam.password) {
//       callApiWithQueryParams(decryptParam.token, decryptParam.username, decryptParam.password);
//     } else {
//       console.warn("No valid search parameter found.");
//       const publicRoutes = [
//         "/login",
//         "/register",
//         "/forgot/username",
//         "/forgot/password",
//         "/forgot/password/create",
//       ];
//       if (!localStorage.getItem("jwtToken") && !publicRoutes.includes(location.pathname)) {
//         navigate("/login", { replace: true });
//       }
//     }
//   }, [location, navigate, handleLogin, dispatch]);

//   useEffect(() => {
//     const handleResize = () => setWindowWidth(window.innerWidth);
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   return (
//     <div
//       style={{
//         display: "flex",
//         flexDirection: "column",
//         minHeight: "100vh",
//         width: "100%",
//       }}
//     >
//       <Suspense fallback={<FullScreenLoader />}>
//         {!isIframe && (
//           <div style={{ width: "100%", position: "fixed", top: 0, zIndex: 1100 }}>
//             <Header />
//           </div>
//         )}
//         <div
//           style={{
//             flex: "1 0 auto",
//             display: "flex",
//             flexDirection: "column",
//             width: "100%",
//             marginTop: isIframe ? 0 : "64px",
//           }}
//         >
//           <Container
//             maxWidth="lg"
//             style={{
//               flex: 1,
//               display: "flex",
//               flexDirection: "column",
//               padding: "0 16px",
//             }}
//           >
//             <Routes>
//               <Route path="/login" element={<Login />} />
//               <Route path="/account/*" element={<Authentication />} />
//               <Route path="/register" element={<Register />} />
//               <Route path="/forgot/username" element={<ForgotUsername />} />
//               <Route path="/forgot/password" element={<ForgotPassword />} />
//               <Route path="/forgot/password/create/" element={<CreatePassword />} />
//               <Route path="/unauthorize" element={<Unauthorize />} />
//               <Route path="/allquestion" element={<Qna />} />
//               <Route path="/add-question" element={<AddQuestion />} />
//               <Route path="/question" element={<ViewQuestion />} />
//               <Route path="/" element={<Home />} />
//               <Route path="/tags" element={<Tags />} />
//               <Route path="/tags/:tagName" element={<TagsDetails />} />
//               <Route path="/userdetails" element={<UserDetails />} />
//               <Route path="/userprofile" element={<UserProfile />} />
//               <Route path="/usersave" element={<UserSaveDetails />} />
//               <Route path="/userhome" element={<UserHome />} />
//               <Route path="/user1" element={<User />} />
//               <Route path="/search/:query" element={<SearchDetails />} />
//               <Route path="*" element={<Unauthorize />} />
//             </Routes>
//           </Container>
//         </div>
//         <div style={{ width: "100%", flexShrink: 0 }}>
//           <Footer />
//         </div>
//       </Suspense>
//     </div>
//   );
// }

// export default RootRoutes;
